import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}

function normalizeExpression(raw) {
  let s = typeof raw === "string" ? raw : "";
  s = s.replace(/^\s*f'?\(x\)\s*=\s*/i, "");
  s = s.replace(/^\s*y\s*=\s*/i, "");
  s = s.replace(/\u2212/g, "-");
  s = s.replace(/(\d),(\d)/g, "$1.$2");
  s = s.replace(/\s+/g, "");
  s = explicitMultiply(s);
  s = s.replace(/\^/g, "**");
  return s;
}

function safeEval(expr, x) {
  const normalized = normalizeExpression(expr);
  if (!normalized) return NaN;
  const js = normalized.replace(/x/g, `(${x})`);
  try {
    const value = Function(`"use strict"; return (${js});`)();
    return Number.isFinite(value) ? value : NaN;
  } catch {
    return NaN;
  }
}

function areEquivalent(userExpr, expectedExpr) {
  const points = [-3, -2, -1, 0, 1, 2, 3];
  for (const x of points) {
    const userVal = safeEval(userExpr, x);
    const expectedVal = safeEval(expectedExpr, x);
    if (!Number.isFinite(userVal) || !Number.isFinite(expectedVal)) return false;
    if (Math.abs(userVal - expectedVal) > 0.001) return false;
  }
  return true;
}

function termLatex(coef, power, isFirst = false) {
  if (coef === 0) return "";
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const signPart = isFirst ? (coef < 0 ? "-" : "") : ` ${sign} `;

  if (power === 0) return `${signPart}${abs}`;
  if (power === 1) {
    if (abs === 1) return `${signPart}x`;
    return `${signPart}${abs}x`;
  }
  if (abs === 1) return `${signPart}x^{${power}}`;
  return `${signPart}${abs}x^{${power}}`;
}

function polynomialLatex(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (nonZero.length === 0) return "0";
  return nonZero.map((t, idx) => termLatex(t.coef, t.power, idx === 0)).join("");
}

function polynomialAscii(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (nonZero.length === 0) return "0";
  return nonZero
    .map((t, idx) => {
      const coef = t.coef;
      const sign = coef < 0 ? "-" : "+";
      const abs = Math.abs(coef);
      const prefix = idx === 0 ? (coef < 0 ? "-" : "") : ` ${sign} `;

      if (t.power === 0) return `${prefix}${abs}`;
      if (t.power === 1) return `${prefix}${abs === 1 ? "x" : `${abs}x`}`;
      return `${prefix}${abs === 1 ? `x^${t.power}` : `${abs}x^${t.power}`}`;
    })
    .join("");
}

function derivativeTerms(terms) {
  return terms
    .filter((t) => t.power > 0)
    .map((t) => ({ coef: t.coef * t.power, power: t.power - 1 }))
    .filter((t) => t.coef !== 0);
}

export function generateTask() {
  const degree = [2, 3, 4][Math.floor(Math.random() * 3)];
  const terms = [];

  for (let power = degree; power >= 0; power--) {
    const exclude = power === degree ? [0] : [];
    const coef = randInt(-6, 6, exclude);
    terms.push({ coef, power });
  }

  const derivative = derivativeTerms(terms);
  const functionLatex = polynomialLatex(terms);
  const derivativeLatex = polynomialLatex(derivative);
  const derivativeAscii = polynomialAscii(derivative);

  return {
    type: "PolynomialDerivativeTask",
    prompt: `Oblicz pochodna funkcji: f(x) = ${functionLatex}. Podaj f'(x).`,
    answer: derivativeAscii,
    data: {
      degree,
      terms,
      functionLatex,
      derivativeLatex,
      derivativeAscii,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Oblicz pochodna funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>Podaj <InlineMath math={"f'(x)"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div><InlineMath math={"f'(x) ="} /></div>
      <EquationInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 6x+1"
        basis="clamp(180px, 28ch, 280px)"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) return false;
  return areEquivalent(userAnswer, data.derivativeAscii);
}

export function renderUserAnswer(userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`f'(x) = ${userAnswer.replace(/\*/g, "\\cdot ")}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f'(x) = ${data.derivativeLatex}`} />;
}

export function renderExplanation({ data }) {
  const derivativeSteps = data.terms.map((t) => {
    const original = termLatex(t.coef, t.power, true).replace(/^\+\s*/, "");
    if (t.power === 0) {
      return { original, result: "0" };
    }

    const resultCoef = t.coef * t.power;
    const resultPower = t.power - 1;
    const result =
      resultPower >= 0
        ? termLatex(resultCoef, resultPower, true).replace(/^\+\s*/, "")
        : "0";
    return { original, result };
  });

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Korzystamy z reguly{" "}
        <InlineMath math={"(ax^n)' = a \\cdot n x^{n-1}"} /> dla kazdego skladnika.
      </p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p><strong>Krok 2:</strong> Rozniczkujemy kolejne skladniki:</p>
      {derivativeSteps.map((step, idx) => (
        <BlockMath key={idx} math={`${step.original} \\rightarrow ${step.result}`} />
      ))}
      <p><strong>Krok 3:</strong> Zapisujemy wynik:</p>
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />
    </div>
  );
}

